// GET   /sinister-negotiation/:sinisterId/interactions
// POST  /sinister-negotiation/interactions               { sinisterId, type, note?, returnAt? }
// GET   /sinister-negotiation/:sinisterId/status-history
// POST  /sinister-negotiation/:sinisterId/status          { status, note, file? } (multipart se houver file)
//       Quando status = "collection_started", backend grava/reinicia deadlineDate = hoje + 20 dias e
//       devolve no response (contrato confirmado, ver PR-07-cobranca-deadline-inicio-atuacao-frontend.md).
// POST  /sinister-negotiation/:sinisterId/deals           { valueIds, paymentMethod, installmentsCount, dueDate, totalValue, downPaymentValue? }
//       downPaymentDate (16/07/2026) ainda não está em nenhum contrato do backend — mandado otimista,
//       igual já foi feito com downPaymentValue antes dele existir de verdade (ver SinisterAgreementService.ts).
//       O repasse para imobiliária NÃO faz parte da negociação — é registrado à parte, por parcela,
//       só depois do acordo fechado (ver SinisterAgreementService.registerAgencyTransfer).
// GET   /sinister-negotiation/:sinisterId/documents
// POST  /sinister-negotiation/:sinisterId/documents       { file } (multipart)
// PATCH /sinister-negotiation/documents/:documentId/remove
// GET   /sinister-negotiation/documents/:documentId/buffer (blob)
// (documentos do caso — conceito separado do attachmentUrl do status-history acima)
// GET   /sinister-negotiation/responsibles?sector=&name=  — contrato confirmado, ver
//       PR-08-cobranca-responsavel-por-setor-frontend.md. Lista de usuários elegíveis a responsável
//       de COBRANÇA, já filtrada por setor de atuação (cobranca | juridico) no backend — é um conceito
//       novo e separado do responsável geral do sinistro (userService.getManagers / sinister.responsibleId).
// PATCH /sinister-negotiation/:sinisterId/responsible     { responsibleId } — idem, conceito novo,
//       não confundir com PATCH /sinister/responsible/:sinisterId (SinisterService.updateResponsible),
//       que é o responsável geral do sinistro, não o responsável da cobrança por setor.
import { Api } from "../axios-config";
import { extractApiErrorMessage } from "../../../utils/extractApiErrorMessage";
import { SinisterSector } from "../../../../pages/creditRecovery/constants/sectorOptions";

// Lista de ações oficial passada pela equipe de operação (ticket "Correção dos status e das ações
// do módulo de cobrança", prioridade alta). Vários códigos coincidem de propósito com os de
// SinisterCaseStatus abaixo — a mesma ação (ex: "Acordo Parcelado") pode existir como resultado de
// uma AÇÃO (aqui, com observação/anexo) e também como um STATUS (sem observação) — confirmado com
// o time de operação, não é duplicidade indevida.
export type NegotiationInteractionType =
	| "installment_agreement" // "Acordo Parcelado"
	| "collection_dropped" // "Cobrança Baixada"
	| "negotiating" // "Em negociação"
	| "forwarded_to_legal" // "Encaminhado para o Jurídico"
	| "forwarded_to_post" // "Encaminhado para o Pós"
	| "keys_delivered" // "Entrega de Chaves"
	| "fraud" // "Fraude"
	| "fraud_suspected" // "Suspeita de Fraude"
	| "no_contact_case" // "Não conseguiu contato"
	| "cash_payment" // "Pagamento a vista (Boleto, Pix, Cartão de Crédito)"
	| "payment_at_agency" // "Pagamento na imobiliária"
	| "keys_delivery_promise" // "Promessa de entrega de chaves"
	| "payment_promise" // "Promessa de Pagamento"
	| "no_agreement_possible" // "Sem Possibilidade de acordo"
	| "finalized_without_indemnity" // "Sinistro Finalizado sem indenização"
	| "spreadsheet_request" // "Solicitação de Planilha"
	| "awaiting_return"; // "Aguardando Retorno" — substitui "return_on_date", mesmo campo extra de data

export interface ISinisterNegotiationInteraction {
	id: string;
	sinisterId: string;
	type: NegotiationInteractionType;
	note?: string;
	returnAt?: string;
	createdAt: string;
	userName?: string;
}

export interface ICreateNegotiationInteraction {
	type: NegotiationInteractionType;
	note?: string;
	returnAt?: string;
}

export type NegotiationPaymentMethod = "boleto" | "pix" | "credit_card";

// "monthly": parcelas caem sempre no mesmo dia do mês seguinte (ex: 05/06, 05/07, 05/08).
// "days": intervalo fixo em dias corridos entre parcelas (ex: a cada 30 dias) — pode "andar" o dia
// do vencimento mês a mês (meses com menos de 30/31 dias). Default deve ser "monthly".
export type NegotiationInstallmentIntervalType = "monthly" | "days";

export interface ICreateNegotiationDeal {
	valueIds: string[];
	paymentMethod: NegotiationPaymentMethod;
	installmentsCount: number;
	installmentIntervalType?: NegotiationInstallmentIntervalType;
	installmentIntervalDays?: number;
	discountPercent?: number;
	dueDate: string;
	totalValue: number;
	downPaymentValue?: number;
	downPaymentDate?: string;
}

export interface ISinisterNegotiationDeal extends ICreateNegotiationDeal {
	id: string;
	sinisterId: string;
	createdAt: string;
}

// Lista de status oficial passada pela equipe de operação (mesmo ticket da nota acima). Mantém o
// código "collection_started" pra "Em atuação" — é o mesmo status que já existia (antes rotulado
// "Início da Atuação de Cobrança"), preserva o efeito colateral de deadlineDate (+20 dias, ver
// comentário no topo do arquivo) sem precisar de coordenação com o backend pra criar um código novo.
export type SinisterCaseStatus =
	| "collection_started" // "Em atuação"
	| "duplicate_case_closed" // "Aberto em Duplicidade - Encerrado"
	| "debt_confession_closed" // "Confissão de Dívidas - Encerrado"
	| "cash_agreement" // "Acordo a Vista"
	| "direct_payment" // "Pagamento direto"
	| "installment_agreement" // "Acordo parcelado"
	| "preventive_agreement" // "Acordo em Preventiva"
	| "partial_payment" // "Pagamento Parcial"
	| "keys_delivered" // "Entrega de Chaves"
	| "agreement_broken" // "Quebra de Acordo"
	| "no_agreement_possible" // "Sem Possibilidade de Acordo"
	| "finalized_pd_gei_sc" // "Finalizado PD/GEI/SC"
	| "recurring_claim_pe" // "Sinistro Recorrente - PE"
	| "finalized" // "Finalizado"
	| "deceased" // "Falecido"
	| "canceled"; // "Cancelado"

export interface ISinisterCaseStatusEntry {
	id: string;
	sinisterId: string;
	status: SinisterCaseStatus;
	note: string;
	attachmentUrl?: string;
	createdAt: string;
	userName?: string;
	// Só é relevante quando status = "collection_started": backend recalcula (createdAt + 20 dias) e retorna
	// o novo valor aqui para o front atualizar o card/linha sem esperar um refetch da listagem.
	deadlineDate?: string | null;
}

export interface IUpdateCaseStatus {
	status: SinisterCaseStatus;
	note: string;
	file?: File;
}

export interface ISinisterCaseDocument {
	id: string;
	originalFilename: string;
	uploadedDate: string;
	url: string;
	mimetype?: string;
}

// Usuário elegível a responsável de cobrança, já filtrado por setor de atuação — conceito novo,
// separado do responsável geral do sinistro (ISinisterList.sinisterResponsible).
export interface ICollectionResponsibleOption {
	id: string;
	name: string;
	sector: SinisterSector;
}

const getInteractions = async (sinisterId: string): Promise<ISinisterNegotiationInteraction[] | Error> => {
	try {
		const { data } = await Api().get(`/sinister-negotiation/${sinisterId}/interactions`);
		return data?.results ?? data ?? [];
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar interações.");
	}
};

const createInteraction = async (sinisterId: string, payload: ICreateNegotiationInteraction): Promise<ISinisterNegotiationInteraction | Error> => {
	try {
		const { data } = await Api().post(`/sinister-negotiation/interactions`, { sinisterId, ...payload });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao registrar interação.");
	}
};

const createDeal = async (sinisterId: string, payload: ICreateNegotiationDeal): Promise<ISinisterNegotiationDeal | Error> => {
	try {
		const { data } = await Api().post(`/sinister-negotiation/${sinisterId}/deals`, payload);
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao registrar acordo de negociação.");
	}
};

const getCaseStatusHistory = async (sinisterId: string): Promise<ISinisterCaseStatusEntry[] | Error> => {
	try {
		const { data } = await Api().get(`/sinister-negotiation/${sinisterId}/status-history`);
		return data?.results ?? data ?? [];
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar histórico de status.");
	}
};

const updateCaseStatus = async (sinisterId: string, payload: IUpdateCaseStatus): Promise<ISinisterCaseStatusEntry | Error> => {
	try {
		const { status, note, file } = payload;

		if (file) {
			const formData = new FormData();
			formData.append("status", status);
			formData.append("note", note);
			formData.append("file", file);

			const { data } = await Api().post(`/sinister-negotiation/${sinisterId}/status`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			return data;
		}

		const { data } = await Api().post(`/sinister-negotiation/${sinisterId}/status`, { status, note });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao registrar status do caso.");
	}
};

const getCaseDocuments = async (sinisterId: string): Promise<ISinisterCaseDocument[] | Error> => {
	try {
		const { data } = await Api().get(`/sinister-negotiation/${sinisterId}/documents`);
		return data?.results ?? data ?? [];
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar documentos da cobrança.");
	}
};

const uploadCaseDocument = async (sinisterId: string, file: File): Promise<ISinisterCaseDocument | Error> => {
	try {
		const formData = new FormData();
		formData.append("file", file);

		const { data } = await Api().post(`/sinister-negotiation/${sinisterId}/documents`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao anexar documento.");
	}
};

const removeCaseDocument = async (documentId: string): Promise<void | Error> => {
	try {
		await Api().patch(`/sinister-negotiation/documents/${documentId}/remove`);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao remover documento.");
	}
};

const previewCaseDocument = async (documentId: string): Promise<Blob | Error> => {
	try {
		const { data } = await Api().get(`/sinister-negotiation/documents/${documentId}/buffer`, { responseType: "blob" });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao visualizar documento.");
	}
};

const getResponsibleOptions = async (sector: SinisterSector, search: string = ""): Promise<ICollectionResponsibleOption[] | Error> => {
	try {
		const { data } = await Api().get(`/sinister-negotiation/responsibles?sector=${sector}&name=${search}`);
		return data?.results ?? data ?? [];
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar responsáveis pela cobrança.");
	}
};

const updateCollectionResponsible = async (
	sinisterId: string,
	responsibleId: string
): Promise<{ id: string; collectionResponsibleId: string; collectionResponsibleName?: string } | Error> => {
	try {
		const { data } = await Api().patch(`/sinister-negotiation/${sinisterId}/responsible`, { responsibleId });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao atualizar responsável pela cobrança.");
	}
};

export type NotifyCollectionResponsibleResult = {
	message: string;
	responsibleId: string;
	notifiedEmail?: string;
};

// notifyCollectionResponsible — endpoint AINDA NÃO existe no backend (pedido formalizado, ver
// GAPS_COBRANCA_STATUS_ACOES_PARA_BACKEND.md, seção 8). Chamada como ação secundária logo após
// updateCollectionResponsible ter sucesso: o backend deve localizar o responsável recém-atribuído
// pelo próprio sinisterId (mesma leitura que acabou de ser gravada no PATCH acima) e disparar o
// e-mail de notificação pra ele. Corpo vazio — sem redundância de responsibleId.
const notifyCollectionResponsible = async (sinisterId: string): Promise<NotifyCollectionResponsibleResult | Error> => {
	try {
		const { data } = await Api().post(`/sinister-negotiation/${sinisterId}/responsible/notify`);
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao notificar o responsável pela cobrança.");
	}
};

export const SinisterNegotiationService = {
	getInteractions,
	createInteraction,
	createDeal,
	getCaseStatusHistory,
	updateCaseStatus,
	getCaseDocuments,
	uploadCaseDocument,
	removeCaseDocument,
	previewCaseDocument,
	getResponsibleOptions,
	updateCollectionResponsible,
	notifyCollectionResponsible,
};
